import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { getTraffic } from '../../services/adminApi';
import { AdminTrafficResponse } from '../../types';
import glassTheme from '../../styles/glassTheme';
import { useAdminTrafficLive } from '../../hooks/useAdminTrafficLive';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const Panel = styled.section`
  color: ${glassTheme.colors.text.primary};
`;

const Heading = styled.div`
  margin-bottom: ${glassTheme.spacing.lg};
  h2 { margin: 0 0 ${glassTheme.spacing.sm}; }
  p { margin: ${glassTheme.spacing.xs} 0; color: ${glassTheme.colors.text.secondary}; }
`;

const HeadingRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${glassTheme.spacing.sm};
`;

const LiveStatus = styled.span<{ $state: 'live' | 'reconnecting' | 'stale' }>`
  color: ${props => props.$state === 'live'
    ? glassTheme.colors.status.success
    : props.$state === 'stale'
      ? glassTheme.colors.status.error
      : glassTheme.colors.status.warning};
  font-weight: 600;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${glassTheme.spacing.lg};
  margin-bottom: ${glassTheme.spacing.lg};
  @media (max-width: 1024px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 768px) { grid-template-columns: 1fr; gap: ${glassTheme.spacing.md}; }
`;

const Card = styled.div`
  ${glassTheme.glass.container}
  padding: ${glassTheme.spacing.lg};
`;

const CardLabel = styled.p`
  margin: 0 0 ${glassTheme.spacing.sm};
  color: ${glassTheme.colors.text.secondary};
`;

const CardValue = styled.p`
  margin: 0;
  font-size: 2.5rem;
  font-weight: bold;
  background: ${glassTheme.gradients.cyber};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const CardHelp = styled.p`
  margin: ${glassTheme.spacing.sm} 0 0;
  color: ${glassTheme.colors.text.muted};
  font-size: 0.85rem;
`;

const Container = styled.div`
  ${glassTheme.glass.container}
  padding: ${glassTheme.spacing.lg};
  margin-bottom: ${glassTheme.spacing.lg};
  overflow-x: auto;
  &:focus { outline: 3px solid rgba(96, 165, 250, 0.3); outline-offset: 2px; }
`;

const ChartArea = styled.div`
  position: relative;
  height: 320px;
  @media (max-width: 768px) { height: 240px; }
`;

const Notice = styled.div<{ $error?: boolean }>`
  ${glassTheme.glass.light}
  padding: ${glassTheme.spacing.md};
  margin-bottom: ${glassTheme.spacing.lg};
  color: ${props => props.$error ? glassTheme.colors.status.error : glassTheme.colors.status.info};
`;

const Button = styled.button`
  padding: 0.8rem 1.5rem;
  background: transparent;
  color: #fff;
  border: 1px solid ${glassTheme.colors.border};
  border-radius: ${glassTheme.borderRadius.sm};
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all ${glassTheme.transition.base};
  &:hover:not(:disabled) { border-color: ${glassTheme.colors.accent.blue}; }
  &:focus-visible { outline: 3px solid rgba(96, 165, 250, 0.3); outline-offset: 2px; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const RetryButton = styled(Button)`
  margin-top: ${glassTheme.spacing.md};
  background: ${glassTheme.gradients.cyber};
  border: none;
`;

const Table = styled.table`
  width: 100%;
  min-width: 760px;
  border-collapse: collapse;
  caption {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
  }
`;

const Th = styled.th<{ $number?: boolean }>`
  padding: ${glassTheme.spacing.md};
  text-align: ${props => props.$number ? 'right' : 'left'};
  color: ${glassTheme.colors.text.secondary};
  border-bottom: 1px solid ${glassTheme.colors.border};
  white-space: nowrap;
`;

const Td = styled.td<{ $number?: boolean }>`
  padding: ${glassTheme.spacing.md};
  text-align: ${props => props.$number ? 'right' : 'left'};
  border-bottom: 1px solid ${glassTheme.colors.border};
  white-space: nowrap;
`;

const Tr = styled.tr`
  &:hover { background: rgba(96, 165, 250, 0.05); }
`;

const Badge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: ${glassTheme.borderRadius.full};
  color: ${glassTheme.colors.accent.blue};
  background: rgba(96, 165, 250, 0.2);
  font-size: 0.8rem;
  font-weight: 600;
`;

const Details = styled.details`
  margin-top: ${glassTheme.spacing.md};
  summary {
    cursor: pointer;
    color: ${glassTheme.colors.accent.blue};
    padding: ${glassTheme.spacing.sm};
  }
  summary:focus-visible { outline: 3px solid rgba(96, 165, 250, 0.3); }
`;

const Pagination = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: ${glassTheme.spacing.md};
  margin-top: ${glassTheme.spacing.lg};
`;

const RoleFilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${glassTheme.spacing.sm};
  margin: ${glassTheme.spacing.md} 0;
`;

const RoleChip = styled.button<{ $active: boolean }>`
  min-height: 44px;
  padding: ${glassTheme.spacing.sm} ${glassTheme.spacing.md};
  border-radius: ${glassTheme.borderRadius.full};
  border: 1px solid ${props => props.$active
    ? glassTheme.colors.accent.blue
    : glassTheme.colors.border};
  background: ${props => props.$active ? 'rgba(96, 165, 250, 0.2)' : 'transparent'};
  color: ${props => props.$active ? glassTheme.colors.text.primary : glassTheme.colors.text.secondary};
  cursor: pointer;
  transition: all ${glassTheme.transition.fast};
  &:focus-visible { outline: 3px solid rgba(96, 165, 250, 0.3); outline-offset: 2px; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const UserLink = styled(Link)`
  color: ${glassTheme.colors.accent.blue};
  text-decoration: underline;
  text-underline-offset: 0.2em;
  &:focus-visible { outline: 3px solid rgba(96, 165, 250, 0.3); outline-offset: 2px; }
`;

const ROLE_LABELS: Record<string, string> = {
  super_admin: '超级管理员',
  admin: '管理员',
  moderator: '内容审核员',
  analyst: '数据分析员',
  content_manager: '内容管理员',
  teacher: '教师',
  student: '学生'
};
const ROLE_ORDER = [
  'student',
  'teacher',
  'super_admin',
  'admin',
  'moderator',
  'analyst',
  'content_manager'
];

const integer = new Intl.NumberFormat('zh-CN');
const utcDate = (value: string) => new Date(value).toISOString().slice(0, 10);
const utcTimestamp = (value: string) =>
  new Date(value).toISOString().replace('T', ' ').slice(0, 19) + ' UTC';

interface Props {
  onAuthError: (error: any) => boolean;
}

export const AdminTrafficPanel: React.FC<Props> = ({ onAuthError }) => {
  const [data, setData] = useState<AdminTrafficResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [requestedPage, setRequestedPage] = useState(1);
  const [error, setError] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const [selectedRole, setSelectedRole] = useState('all');
  const requestId = useRef(0);
  const requestController = useRef<AbortController | null>(null);
  const pageRef = useRef(1);
  const roleRef = useRef('all');

  const load = useCallback(async (
    page: number,
    role: string,
    mode: 'initial' | 'paging' | 'background' = 'initial'
  ): Promise<boolean> => {
    const currentRequest = ++requestId.current;
    requestController.current?.abort();
    const controller = new AbortController();
    requestController.current = controller;
    setLoading(true);
    setError(false);
    try {
      const response = await getTraffic(page, 20, role, controller.signal);
      if (currentRequest !== requestId.current) return false;
      setData(response);
      setRequestedPage(response.pagination.page);
      pageRef.current = response.pagination.page;
      const roleName = role === 'all' ? '全部' : (ROLE_LABELS[role] || role);
      setAnnouncement(mode === 'paging'
        ? `已加载${roleName}第 ${response.pagination.page} 页`
        : mode === 'initial' ? '流量数据已更新' : '用户流量已更新');
      return true;
    } catch (requestError: any) {
      if (requestError?.code === 'ERR_CANCELED') return false;
      if (!onAuthError(requestError)) setError(true);
      return false;
    } finally {
      if (currentRequest === requestId.current) setLoading(false);
    }
  }, [onAuthError]);

  useEffect(() => {
    void load(1, 'all');
    return () => requestController.current?.abort();
  }, [load]);

  const refreshLive = useCallback(
    () => load(pageRef.current, roleRef.current, 'background'),
    [load]
  );
  const { state: liveState, lastSyncedAt, markSynced } = useAdminTrafficLive({
    refresh: refreshLive,
    shouldRefresh: event => event.type === 'navigation.page_view',
    onAuthError
  });
  const syncedGeneration = data?.generatedAt;
  useEffect(() => {
    if (syncedGeneration) markSynced();
  }, [syncedGeneration, markSynced]);

  const chartData = useMemo(() => ({
    labels: data?.daily.map(day => day.date.slice(5)) || [],
    datasets: [
      {
        label: '浏览量',
        data: data?.daily.map(day => day.pageViews) || [],
        borderColor: glassTheme.colors.accent.blue,
        backgroundColor: glassTheme.colors.accent.blue,
        tension: 0.2
      },
      {
        label: '活跃用户',
        data: data?.daily.map(day => day.activeUsers) || [],
        borderColor: glassTheme.colors.accent.purple,
        backgroundColor: glassTheme.colors.accent.purple,
        tension: 0.2
      }
    ]
  }), [data]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? false as const : undefined,
    interaction: { mode: 'index' as const, intersect: false },
    plugins: {
      legend: { labels: { color: glassTheme.colors.text.secondary } },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        callbacks: {
          title: (items: any[]) => {
            const index = items[0]?.dataIndex;
            return index === undefined || !data ? '' : `${data.daily[index].date}（UTC）`;
          }
        }
      }
    },
    scales: {
      x: {
        ticks: { color: glassTheme.colors.text.secondary },
        grid: { color: glassTheme.colors.border }
      },
      y: {
        beginAtZero: true,
        ticks: { color: glassTheme.colors.text.secondary, precision: 0 },
        grid: { color: glassTheme.colors.border }
      }
    }
  }), [data]);

  if (loading && !data) {
    return (
      <Panel role="status" aria-live="polite" aria-busy="true">
        正在加载流量数据…
      </Panel>
    );
  }

  if (error && !data) {
    return (
      <Panel>
        <Notice $error role="alert">
          流量数据加载失败，请稍后重试。
          <div><RetryButton onClick={() => void load(requestedPage, selectedRole)}>重试</RetryButton></div>
        </Notice>
      </Panel>
    );
  }

  if (!data) return null;

  const cards = [
    ['浏览量', integer.format(data.summary.pageViews), '已记录的页面浏览次数'],
    ['活跃用户', integer.format(data.summary.activeUsers), '至少浏览一次的当前用户'],
    ['会话数', integer.format(data.summary.sessions), '不同的非空会话'],
    ['人均浏览量', data.summary.averageViewsPerActiveUser.toFixed(2), '每位活跃用户的平均浏览次数']
  ];
  const groupByRole = new Map(data.roleGroups.map(group => [group.role, group]));
  const allGroup = data.roleGroups.reduce((total, group) => ({
    totalAccounts: total.totalAccounts + group.totalAccounts,
    activeAccounts: total.activeAccounts + group.activeAccounts
  }), { totalAccounts: 0, activeAccounts: 0 });
  const visibleGroups = [
    { role: 'all', ...allGroup },
    ...ROLE_ORDER.map(role => ({
      role,
      totalAccounts: groupByRole.get(role)?.totalAccounts || 0,
      activeAccounts: groupByRole.get(role)?.activeAccounts || 0
    }))
  ];
  const liveText = liveState === 'live'
    ? '实时更新'
    : liveState === 'stale'
      ? `数据可能已过期 · 上次同步 ${lastSyncedAt ? utcTimestamp(lastSyncedAt.toISOString()).slice(11) : '未成功'}`
      : '正在重连';

  return (
    <Panel aria-busy={loading}>
      <span aria-live="polite" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden' }}>
        {announcement}。{liveText}
      </span>
      <Heading>
        <HeadingRow>
          <h2>近 30 天流量</h2>
          <LiveStatus $state={liveState}>{liveText}</LiveStatus>
        </HeadingRow>
        <p>{data.definition}</p>
        <p>统计范围：{utcDate(data.window.start)} 至 {utcDate(data.window.end)}（UTC）</p>
        <p>生成于：{utcTimestamp(data.generatedAt)}</p>
      </Heading>

      {data.summary.pageViews === 0 && (
        <Notice>
          近 30 天暂无已记录的登录用户页面浏览。流量从功能启用后开始记录，不会补录历史访问。
        </Notice>
      )}

      <SummaryGrid>
        {cards.map(([label, value, help]) => (
          <Card key={label}>
            <CardLabel>{label}</CardLabel>
            <CardValue>{value}</CardValue>
            <CardHelp>{help}</CardHelp>
          </Card>
        ))}
      </SummaryGrid>

      <Container>
        <h2>每日流量趋势（UTC）</h2>
        <ChartArea aria-hidden="true">
          <Line data={chartData} options={chartOptions} />
        </ChartArea>
        <Details>
          <summary>查看每日数据表</summary>
          <Table>
            <caption>最近 30 个 UTC 日的浏览量和活跃用户</caption>
            <thead><tr><Th scope="col">日期（UTC）</Th><Th scope="col" $number>浏览量</Th><Th scope="col" $number>活跃用户</Th></tr></thead>
            <tbody>
              {data.daily.map(day => (
                <Tr key={day.date}>
                  <Td>{day.date}</Td>
                  <Td $number>{integer.format(day.pageViews)}</Td>
                  <Td $number>{integer.format(day.activeUsers)}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </Details>
      </Container>

      <h2>用户流量</h2>
      <p>每个账户仅按一个报告角色归组，多角色账户不会重复计数。活跃指近 30 天内至少有一次已记录页面浏览。</p>
      <RoleFilterBar role="group" aria-label="按报告角色筛选用户流量">
        {visibleGroups.map(group => {
          const label = group.role === 'all' ? '全部' : ROLE_LABELS[group.role];
          return (
            <RoleChip
              key={group.role}
              type="button"
              $active={selectedRole === group.role}
              aria-pressed={selectedRole === group.role}
              aria-label={`${label}：账户 ${group.totalAccounts}，近 30 天活跃 ${group.activeAccounts}`}
              disabled={loading}
              onClick={() => {
                setSelectedRole(group.role);
                roleRef.current = group.role;
                pageRef.current = 1;
                setRequestedPage(1);
                void load(1, group.role, 'paging');
              }}
            >
              {label} 账户 {integer.format(group.totalAccounts)} · 活跃 {integer.format(group.activeAccounts)}
            </RoleChip>
          );
        })}
      </RoleFilterBar>
      <Container
        tabIndex={0}
        aria-label="用户流量表（可横向滚动）"
        aria-busy={loading}
      >
        {loading && <p role="status">正在更新用户流量…</p>}
        {error && data && (
          <Notice $error role="alert">
            流量数据加载失败，请稍后重试。
            <div><RetryButton onClick={() => void load(requestedPage, selectedRole)}>重试</RetryButton></div>
          </Notice>
        )}
        <Table>
          <caption>当前用户在固定 30 日窗口内的页面浏览汇总</caption>
          <thead>
            <tr>
              <Th scope="col">用户</Th><Th scope="col">邮箱</Th><Th scope="col">角色</Th>
              <Th scope="col" $number>浏览量</Th><Th scope="col" $number>会话数</Th>
              <Th scope="col" $number>活跃天数</Th><Th scope="col">最后浏览时间</Th>
            </tr>
          </thead>
          <tbody>
            {data.users.length === 0 ? (
              <Tr><Td colSpan={7}>
                {selectedRole === 'all'
                  ? '暂无用户账户'
                  : `暂无${ROLE_LABELS[selectedRole] || selectedRole}账户`}
              </Td></Tr>
            ) : data.users.map(user => (
              <Tr key={user.id}>
                <Td>
                  <UserLink to={`/admin/traffic/users/${encodeURIComponent(user.id)}`}>
                    {user.displayName || '未设置名称'}
                  </UserLink>
                </Td>
                <Td>{user.email}</Td>
                <Td><Badge>{ROLE_LABELS[user.reportingRole] || user.reportingRole || '未设置'}</Badge></Td>
                <Td $number>{integer.format(user.pageViews)}</Td>
                <Td $number>{integer.format(user.sessions)}</Td>
                <Td $number>{integer.format(user.activeDays)}</Td>
                <Td>{user.lastSeen ? utcTimestamp(user.lastSeen) : '暂无浏览'}</Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      </Container>

      <Pagination aria-live="polite">
        <Button
          disabled={loading || data.pagination.page <= 1}
          onClick={() => {
            const page = data.pagination.page - 1;
            setRequestedPage(page);
            pageRef.current = page;
            void load(page, selectedRole, 'paging');
          }}
        >上一页</Button>
        <span>第 {data.pagination.page} / {data.pagination.pages} 页</span>
        <Button
          disabled={loading || data.pagination.page >= data.pagination.pages}
          onClick={() => {
            const page = data.pagination.page + 1;
            setRequestedPage(page);
            pageRef.current = page;
            void load(page, selectedRole, 'paging');
          }}
        >下一页</Button>
      </Pagination>
    </Panel>
  );
};
